import { useEffect } from "react";
import { AppLoaderProps } from "./AppLoaderProps";
import { customerAuthSession } from '@api/customerAuthSession';
import { useGetCurrentUserOrganisationsQuery, useGetCurrentUserQuery } from "@api/users";
import { useGetInternalRolesPermissionsQuery, useGetInternalRolesQuery } from "@api/roles";
import { useDispatch, useSelector } from "react-redux";
import { setTokenId, setUserId } from "@store/slices/authSlice";
import Title, { TitleTag, TitleVariant } from "@shared/components/typography/Title";
import Loader from "@shared/components/Loader/Loader";
import Container from "@shared/components/Container/Container";
import { Tag } from "@shared/common/types/tags";
import styles from './AppLoader.module.scss';
import { useGetPermissionsQuery } from "@api/permissions";
import { selectOrganisationContextId } from "@selectors/appSelector";
import { useGetOrganisationThemeQuery } from "@api/organisations";
import { skipToken } from "@reduxjs/toolkit/query";
import { setOrganisationContextId } from "@store/slices/appSlice";

const AppLoader = (props: AppLoaderProps): JSX.Element => {
    const dispatch = useDispatch();
    const accountData = customerAuthSession.getAccount();
    const localAccountId = accountData?.localAccountId ?? null;
    const organisationContextId = useSelector(selectOrganisationContextId);

    useEffect(() => {
        const token = accountData?.idToken;
        if (token) dispatch(setTokenId(token));
        if (accountData?.localAccountId) dispatch(setUserId(accountData.localAccountId));
    }, [dispatch, accountData?.idToken, accountData?.localAccountId]);

    const {
        data: organisationDiscovery,
        isLoading: isLoadingOrganisationDiscovery,
    } = useGetCurrentUserOrganisationsQuery(undefined, { skip: !localAccountId });
    const allowedOrganisations = organisationDiscovery?.organisations ?? [];
    const selectedOrganisationId = allowedOrganisations.some(({ id }) => id === organisationContextId)
        ? organisationContextId
        : allowedOrganisations[0]?.id ?? null;

    useEffect(() => {
        if (organisationDiscovery && selectedOrganisationId !== organisationContextId) {
            dispatch(setOrganisationContextId(selectedOrganisationId));
        }
    }, [dispatch, organisationContextId, organisationDiscovery, selectedOrganisationId]);

    const hasStoredVerifiedOrganisation = Boolean(
        selectedOrganisationId && selectedOrganisationId === organisationContextId,
    );
    const userQuery = localAccountId && selectedOrganisationId && hasStoredVerifiedOrganisation
        ? { organisationId: selectedOrganisationId }
        : skipToken;
    const { isLoading: isLoadingUser } = useGetCurrentUserQuery(userQuery);
    const { isLoading: isLoadingRoles } = useGetInternalRolesQuery(undefined, { skip: !localAccountId });
    const { isLoading: isLoadingRolesPermissions } = useGetInternalRolesPermissionsQuery(undefined, { skip: !localAccountId });
    const { isLoading: isLoadingPermissionsData } = useGetPermissionsQuery(undefined, { skip: !localAccountId });
    useGetOrganisationThemeQuery(organisationContextId!, { skip: !organisationContextId, refetchOnMountOrArgChange: true })

    if (
        isLoadingOrganisationDiscovery
        || (Boolean(selectedOrganisationId) && !hasStoredVerifiedOrganisation)
        || isLoadingUser
        || isLoadingRoles
        || isLoadingRolesPermissions
        || isLoadingPermissionsData
    ) {
        return (
            <Container
                as={Tag.Main}
                classes={styles.wrapper}
            >
                <Title tag={TitleTag.h1} classes={styles.title} variant={TitleVariant.main}>
                    Loading data
                </Title>
                <Title variant={TitleVariant.sectionSubtitle} tag={TitleTag.h2}>
                    Please wait while we prepare <strong>system</strong> for use
                </Title>
                <div className={styles.svg}>
                    <Loader />
                </div>
            </Container>
        );
    }

    return (
        <>
            {props.children}
        </>
    );
}

export default AppLoader;
